import { apiClient } from '../client';
import type { ApiResponse } from '../types';
import { unwrapResponse } from '../types';
import type {
  Job,
  CreateJobInput,
  UpdateJobInput,
  CopyJobInput,
  ListJobsRequest,
  PaginatedResponse,
} from '@coi-live/shared-types';

/**
 * Build query string from request parameters.
 */
function buildQueryString(params: ListJobsRequest): string {
  const searchParams = new URLSearchParams();

  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.complianceStatus && params.complianceStatus !== 'all') {
    searchParams.set('complianceStatus', params.complianceStatus);
  }
  if (params.search) searchParams.set('search', params.search);
  if (params.isOpen !== undefined) searchParams.set('isOpen', String(params.isOpen));
  if (params.insuredId) searchParams.set('insuredId', params.insuredId);
  if (params.riskRequirementId) searchParams.set('riskRequirementId', params.riskRequirementId);

  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Jobs API service.
 * Provides CRUD operations for Job entities.
 */
export const jobsService = {
  /**
   * List jobs with filters and pagination.
   */
  async list(params: ListJobsRequest = {}): Promise<PaginatedResponse<Job>> {
    const url = `/jobs${buildQueryString(params)}`;
    const { data } = await apiClient.get<ApiResponse<PaginatedResponse<Job>>>(url);
    return unwrapResponse(data);
  },

  /**
   * Get a single job by ID.
   */
  async get(id: string): Promise<Job> {
    const { data } = await apiClient.get<ApiResponse<Job>>(`/jobs/${id}`);
    return unwrapResponse(data);
  },

  /**
   * Create a new job.
   */
  async create(input: CreateJobInput): Promise<Job> {
    const { data } = await apiClient.post<ApiResponse<Job>>('/jobs', input);
    return unwrapResponse(data);
  },

  /**
   * Update an existing job.
   */
  async update(id: string, input: UpdateJobInput): Promise<Job> {
    const { data } = await apiClient.put<ApiResponse<Job>>(`/jobs/${id}`, input);
    return unwrapResponse(data);
  },

  /**
   * Delete a job.
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/jobs/${id}`);
  },

  /**
   * Copy an existing job, creating a new job with cloned documents.
   */
  async copy(sourceJobId: string, input: CopyJobInput): Promise<Job> {
    const { data } = await apiClient.post<ApiResponse<Job>>(`/jobs/${sourceJobId}/copy`, input);
    return unwrapResponse(data);
  },

  /**
   * Set or clear the manual compliance override on a job.
   * - To set: pass { isCompliant: boolean, reason?: string }
   * - To clear: pass { clear: true }
   */
  async setComplianceOverride(
    jobId: string,
    body: { isCompliant?: boolean; reason?: string; clear?: boolean }
  ): Promise<Job> {
    const { data } = await apiClient.post<ApiResponse<Job>>(
      `/jobs/${jobId}/compliance-override`,
      body
    );
    return unwrapResponse(data);
  },
};
